import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NavSettings, SitePages } from "@/lib/api";

const LOGO_URL = "/atc-logo.png";

const BUILTIN_DEFS = {
  home:      { to: "/",           label: "Home" },
  equipment: { to: "/equipment", label: "Equipment" },
  services:  { to: "/#services",  label: "Services" },
  contact:   { to: "/contact",    label: "Contact" },
};
const DEFAULT_ORDER = ["home", "equipment", "services", "contact"];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({
    show_home: true, show_equipment: true, show_services: true,
    show_contact: true, show_quote_button: true,
    powered_by_text: "Powered by:", powered_by_logo_url: "",
    powered_by_blend: "none",
    order: DEFAULT_ORDER,
  });
  const [customPages, setCustomPages] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try { setSettings(await NavSettings.get()); } catch {}
      try {
        const all = await SitePages.list(true);
        setCustomPages(all.filter((p) => p.show_in_nav));
      } catch {}
    })();
  }, []);

  const handleClick = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const hash = to.slice(1);
      if (location.pathname !== "/") navigate("/" + hash);
      else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(to);
    }
  };

  // Build ordered list of visible items
  const items = useMemo(() => {
    const known = {};
    Object.entries(BUILTIN_DEFS).forEach(([key, def]) => {
      if (settings[`show_${key}`]) {
        known[key] = { kind: "builtin", id: key, ...def };
      }
    });
    customPages.forEach((p) => {
      known[`page:${p.id}`] = {
        kind: "page", id: `page:${p.id}`,
        to: `/p/${p.slug}`, label: p.title, slug: p.slug,
      };
    });
    const order = Array.isArray(settings.order) && settings.order.length
      ? settings.order
      : DEFAULT_ORDER;
    const seen = new Set();
    const out = [];
    order.forEach((id) => {
      if (known[id] && !seen.has(id)) { out.push(known[id]); seen.add(id); }
    });
    Object.values(known).forEach((it) => {
      if (!seen.has(it.id)) out.push(it);
    });
    return out;
  }, [settings, customPages]);

  const renderItem = (it, mobile = false) => {
    const cls = mobile
      ? "block w-full text-left text-slate-200 py-2 text-sm"
      : "text-sm text-slate-300 hover:text-white transition-colors tracking-wide whitespace-nowrap";
    const testId = mobile
      ? (it.kind === "builtin" ? `nav-mobile-${it.label.toLowerCase()}` : `nav-mobile-page-${it.slug}`)
      : (it.kind === "builtin" ? `nav-link-${it.label.toLowerCase()}` : `nav-page-${it.slug}`);
    if (it.kind === "page" || (it.kind === "builtin" && !it.to.startsWith("/#"))) {
      return (
        <Link key={it.id} to={it.to} onClick={() => setOpen(false)} className={cls} data-testid={testId}>
          {it.label}
        </Link>
      );
    }
    return (
      <button key={it.id} onClick={() => handleClick(it.to)} className={cls} data-testid={testId}>
        {it.label}
      </button>
    );
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-slate-950/60 border-b border-white/5">
      <div className="w-full px-5 sm:px-6 lg:px-8 xl:px-10 min-h-20 py-3 flex items-center gap-4 sm:gap-6">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link">
          <img src={LOGO_URL} alt="ATC Mold and Water" className="h-12 w-12 object-contain" />
          <div className="hidden sm:block leading-tight" data-testid="nav-brand-block">
            <div className="font-display font-black text-sm sm:text-base text-white tracking-tight whitespace-nowrap">
              ATC Mold and Water
            </div>
            {(settings.powered_by_text || settings.powered_by_logo_url) && (
              <div className="flex items-center gap-1.5 mt-1" data-testid="nav-powered-by">
                {settings.powered_by_text && (
                  <span className="text-[8px] uppercase tracking-[0.14em] text-slate-400 whitespace-nowrap">
                    {settings.powered_by_text}
                  </span>
                )}
                {settings.powered_by_logo_url && (() => {
                  const blend = settings.powered_by_blend || "none";
                  const useBackdrop = blend === "multiply" || blend === "screen";
                  return (
                    <span className={`inline-flex ${useBackdrop ? "bg-slate-950 rounded-sm px-1.5 py-0.5" : ""}`}>
                      <img
                        src={settings.powered_by_logo_url}
                        alt="Powered by"
                        className="h-6 w-auto max-w-[180px] object-contain"
                        style={blend !== "none" ? { mixBlendMode: blend } : undefined}
                        data-testid="nav-powered-by-logo"
                      />
                    </span>
                  );
                })()}
              </div>
            )}
          </div>
        </Link>

        <nav className="hidden lg:flex flex-1 min-w-0 flex-wrap items-center justify-around gap-x-3 xl:gap-x-5 gap-y-2 px-2">
          {items.map((it) => renderItem(it, false))}
        </nav>

        <div className="flex items-center gap-3 shrink-0 ml-auto lg:ml-0">
          {settings.show_quote_button && (
            <button
              onClick={() => handleClick("/contact")}
              className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white brand-gradient hover:opacity-90 transition whitespace-nowrap"
              data-testid="nav-quote-button"
            >
              Request Quote
            </button>
          )}
          <button
            className="lg:hidden text-slate-200"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/5 bg-slate-950/95 px-6 py-4 space-y-3">
          {items.map((it) => renderItem(it, true))}
          {settings.show_quote_button && (
            <button
              onClick={() => handleClick("/contact")}
              className="w-full mt-2 px-5 py-3 rounded-full text-sm font-semibold text-white brand-gradient"
            >
              Request Quote
            </button>
          )}
        </div>
      )}
    </header>
  );
};

export default Navbar;
